import React, { useMemo, useState, useEffect } from "react";
import "./productsList.css";
import {
  FiSearch,
  FiPlus,
  FiFilter,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiChevronRight,
  FiRefreshCw,
} from "react-icons/fi";
import { BiExport } from "react-icons/bi";
import ProductForm from "./productForm";

const API_BASE = process.env.REACT_APP_EP || "http://localhost:5000";

const PAGE_SIZE = 10;

const SIBLING_COUNT = 1;
const DOTS = "...";

const range = (start, end) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

const getPaginationRange = (currentPage, totalPages, siblingCount = SIBLING_COUNT) => {
  if (totalPages <= 1) return [1];

  const totalPageNumbers = siblingCount * 2 + 5; // first, last, current, 2*siblings, 2*dots

  // If the number of pages is less than what we want to show, show all pages
  if (totalPageNumbers >= totalPages) {
    return range(1, totalPages);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < totalPages - 2;

  const firstPageIndex = 1;
  const lastPageIndex = totalPages;

  // No left dots, but right dots
  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = range(1, leftItemCount);
    return [...leftRange, DOTS, lastPageIndex];
  }

  // Left dots, but no right dots
  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = range(totalPages - rightItemCount + 1, totalPages);
    return [firstPageIndex, DOTS, ...rightRange];
  }

  // Both left and right dots
  if (shouldShowLeftDots && shouldShowRightDots) {
    const middleRange = range(leftSiblingIndex, rightSiblingIndex);
    return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
  }

  return range(1, totalPages);
};

const emptyProduct = {
  id: null,
  name: "",
  description: "",
  basePrice: "",
  discountType: "",
  discountPercent: "",
  taxClass: "GST",
  vatPercent: "",
  sku: "",
  barcode: "",
  quantity: "",
  weight: "",
  height: "",
  length: "",
  width: "",
  category: "",
  categoryId: null,
  tags: [],
  status: "draft",
  images: [],
};

const calcFinalPrice = (form) => {
  const base = Number(form?.basePrice || form?.base_price || 0);
  if (!base || Number.isNaN(base)) return 0;

  const discountType = form?.discountType || form?.discount_type || "";
  const discountValue = Number(form?.discountPercent || form?.discount_percentage || 0);

  let priceAfterDiscount = base;
  if (discountType === "percentage" && discountValue > 0) {
    priceAfterDiscount = Math.max(0, base - (base * discountValue) / 100);
  } else if (discountType === "fixed" && discountValue > 0) {
    priceAfterDiscount = Math.max(0, base - discountValue);
  }

  return Math.round(priceAfterDiscount * 100) / 100;
};

const getCategoryName = (category) => {
  if (!category) return "";
  if (typeof category === "string") return category;
  if (typeof category === "object") {
    return category.category_name || category.name || "";
  }
  return "";
};

const getCategoryId = (category) => {
  if (!category) return null;
  if (typeof category === "object") {
    return category.category_id || category.id || null;
  }
  return null;
};

const extractErrorMessage = (data) => {
  if (!data) return "An error occurred";
  if (typeof data === "string") return data;

  if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
    const messages = data.errors.map((err) => {
      if (err.msg && err.path) return `${err.path}: ${err.msg}`;
      if (err.msg) return err.msg;
      if (err.message && err.field) return `${err.field}: ${err.message}`;
      if (err.message) return err.message;
      if (err.param && err.msg) return `${err.param}: ${err.msg}`;
      if (typeof err === "string") return err;
      return JSON.stringify(err);
    });
    return messages.join(" | ");
  }

  if (data.message) return data.message;
  if (data.error) {
    return typeof data.error === "string" ? data.error : JSON.stringify(data.error);
  }

  return JSON.stringify(data);
};

const Products = () => {
  const [mode, setMode] = useState("table");
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [lightboxImage, setLightboxImage] = useState(null);

  // --------- FETCH PRODUCTS ---------

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/products`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch products");
      }

      const data = await response.json();
      const productsList = data.data || data.products || data || [];

      console.log("Fetched products:", productsList); // Debug - check category_name

      const transformed = productsList.map((p) => ({
        id: p?.sku || p?.id || "",
        sku: p?.sku || "",
        name: p?.product_name || "",
        description: p?.description || "",
        basePrice: p?.base_price || "",
        discountType: p?.discount_type || "",
        discountPercent: p?.discount_percentage || "",
        taxClass: p?.tax_class || "GST",
        vatPercent: p?.vat_percentage || "",
        barcode: p?.barcode || "",
        quantity: p?.quantity || "",
        weight: p?.weight || "",
        height: p?.height || "",
        length: p?.length || "",
        width: p?.width || "",
        // ✅ FIX: Use category_name directly from API response
        category: p?.category_name || "",
        categoryId: p?.category_id || null,
        tags: Array.isArray(p?.tags) ? p.tags : [],
        status: p?.product_status === true || p?.product_status === "t" ? "published" : "draft",
        images: Array.isArray(p?.images) ? p.images : [],
        imageUrl: p?.image_url || "",
        price: Number(p?.base_price) || 0,
        stock: Number(p?.quantity) || 0,
      }));

      setProducts(transformed);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // --------- CREATE PRODUCT ---------
  const createProduct = async (productData) => {
    setSaving(true);
    setError(null);

    try {
      const formDataToSend = new FormData();

      formDataToSend.append("sku", (productData.sku || "").trim());
      formDataToSend.append("product_name", (productData.name || "").trim());
      formDataToSend.append("description", productData.description || "");
      formDataToSend.append("base_price", Number(productData.basePrice) || 0);

      // discount_type must be: percentage, fixed, null, or empty
      const discountType = !productData.discountType || productData.discountType === "none"
        ? ""
        : productData.discountType;
      formDataToSend.append("discount_type", discountType);

      formDataToSend.append("discount_percentage", Number(productData.discountPercent) || 0);
      formDataToSend.append("tax_class", productData.taxClass || "GST");
      formDataToSend.append("vat_percentage", Number(productData.vatPercent) || 0);
      formDataToSend.append("barcode", productData.barcode || "");
      formDataToSend.append("quantity", Number(productData.quantity) || 0);
      formDataToSend.append("weight", productData.weight || "");
      formDataToSend.append("height", productData.height || "");
      formDataToSend.append("length", productData.length || "");
      formDataToSend.append("width", productData.width || "");

      if (productData.categoryId) {
        formDataToSend.append("category_id", Number(productData.categoryId));
      }

      formDataToSend.append("tags", JSON.stringify(productData.tags || []));
      formDataToSend.append("product_status", productData.status === "published");

      if (productData.images?.[0]?.file) {
        formDataToSend.append("image", productData.images[0].file);
      }

      console.log("=== Sending to API ===");
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }

      const response = await fetch(`${API_BASE}/api/products`, {
        method: "POST",
        body: formDataToSend,
      });

      const data = await response.json();
      console.log("API Response:", data);

      if (data.errors) {
        console.log("Validation Errors:", data.errors);
      }

      if (!response.ok) {
        throw new Error(extractErrorMessage(data));
      }

      const newProduct = data.data || data.product || data;

      const finalPrice = calcFinalPrice(productData);
      const quantity = Number(productData.quantity || 0);
      const imageUrl = productData.images?.[0]?.url || newProduct?.image_url || "";

      const transformed = {
        id: newProduct?.sku || productData.sku || Date.now(),
        sku: newProduct?.sku || productData.sku,
        name: productData.name,
        description: productData.description,
        basePrice: productData.basePrice,
        discountType: productData.discountType,
        discountPercent: productData.discountPercent,
        taxClass: productData.taxClass,
        vatPercent: productData.vatPercent,
        barcode: productData.barcode,
        quantity: productData.quantity,
        weight: productData.weight,
        height: productData.height,
        length: productData.length,
        width: productData.width,
        category: productData.category,
        categoryId: productData.categoryId,
        tags: productData.tags,
        status: productData.status,
        images: productData.images,
        imageUrl,
        price: finalPrice,
        stock: quantity,
      };

      setProducts((prev) => [transformed, ...prev]);
      return transformed;
    } catch (err) {
      console.error("Create product error:", err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // --------- UPDATE PRODUCT ---------
  const updateProduct = async (sku, productData) => {
    setSaving(true);
    setError(null);

    try {
      const formDataToSend = new FormData();

      formDataToSend.append("product_name", (productData.name || "").trim());
      formDataToSend.append("description", productData.description || "");
      formDataToSend.append("base_price", Number(productData.basePrice) || 0);

      const discountType = !productData.discountType || productData.discountType === "none"
        ? ""
        : productData.discountType;
      formDataToSend.append("discount_type", discountType);

      formDataToSend.append("discount_percentage", Number(productData.discountPercent) || 0);
      formDataToSend.append("tax_class", productData.taxClass || "GST");
      formDataToSend.append("vat_percentage", Number(productData.vatPercent) || 0);
      formDataToSend.append("barcode", productData.barcode || "");
      formDataToSend.append("quantity", Number(productData.quantity) || 0);
      formDataToSend.append("weight", productData.weight || "");
      formDataToSend.append("height", productData.height || "");
      formDataToSend.append("length", productData.length || "");
      formDataToSend.append("width", productData.width || "");

      if (productData.categoryId) {
        formDataToSend.append("category_id", Number(productData.categoryId));
      }

      formDataToSend.append("tags", JSON.stringify(productData.tags || []));
      formDataToSend.append("product_status", productData.status === "published");

      const newImage = productData.images?.find((img) => img.file);
      if (newImage?.file) {
        formDataToSend.append("image", newImage.file);
      }

      const response = await fetch(`${API_BASE}/api/products/${sku}`, {
        method: "PUT",
        body: formDataToSend,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(extractErrorMessage(data));
      }

      const updatedProduct = data.data || data.product || data;

      const finalPrice = calcFinalPrice(productData);
      const quantity = Number(productData.quantity || 0);
      const imageUrl = productData.images?.[0]?.url || updatedProduct?.image_url || "";

      const transformed = {
        ...productData,
        id: productData.id || sku,
        imageUrl,
        price: finalPrice,
        stock: quantity,
      };

      setProducts((prev) => prev.map((p) => (p.sku === sku ? transformed : p)));

      return transformed;
    } catch (err) {
      console.error("Update product error:", err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  // --------- DELETE PRODUCT ---------
  const deleteProduct = async (sku) => {
    if (!window.confirm("Are you sure you want to delete this product?")) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/products/${sku}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(data) || "Failed to delete product");
      }

      setProducts((prev) => prev.filter((p) => p.sku !== sku && p.id !== sku));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --------- PAGINATION & FILTERING ---------
  const { currentItems, totalItems, totalPages, safePage, startIndex } = useMemo(() => {
    const term = (searchTerm || "").trim().toLowerCase();

    const filtered = term
      ? products.filter((p) => {
        const name = (p?.name || "").toLowerCase();
        const category = (p?.category || "").toLowerCase();
        const sku = (p?.sku || "").toLowerCase();
        return name.includes(term) || category.includes(term) || sku.includes(term);
      })
      : products;

    const total = filtered.length;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safe = Math.min(currentPage, pages);
    const start = (safe - 1) * PAGE_SIZE;
    const slice = filtered.slice(start, start + PAGE_SIZE);

    return {
      currentItems: slice,
      totalItems: total,
      totalPages: pages,
      safePage: safe,
      startIndex: start,
    };
  }, [products, searchTerm, currentPage]);

  const paginationRange = getPaginationRange(safePage, totalPages);

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return "";
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    return `${API_BASE}${imageUrl}`;
  };

  const openLightbox = (imageUrl, name) => {
    if (!imageUrl) return;
    setLightboxImage({
      src: getImageUrl(imageUrl),
      alt: name || "Product image",
    });
  };

  const closeLightbox = () => setLightboxImage(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeLightbox();
      }
    };

    if (lightboxImage) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lightboxImage]);

  // --------- HANDLERS ---------
  const handleAddProductClick = () => {
    setEditingProduct(null);
    setError(null);
    setMode("form");
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setError(null);
    setMode("form");
  };

  const handleCancelForm = () => {
    setEditingProduct(null);
    setError(null);
    setMode("table");
  };

  const handleSaveProduct = async (form) => {
    try {
      if (form.id && form.sku) {
        await updateProduct(form.sku, form);
      } else {
        await createProduct(form);
      }

      setCurrentPage(1);
      setEditingProduct(null);
      setMode("table");
    } catch (err) {
      console.error("Save product error:", err);
    }
  };

  // In handleDeleteProduct - add debug logging
  const handleDeleteProduct = (product) => {
    console.log("=== DELETE DEBUG ===");
    console.log("Full product:", product);
    console.log("product.sku:", product.sku);
    console.log("product.id:", product.id);

    const identifier = product.sku || product.id;
    console.log("Sending to API:", identifier);

    deleteProduct(identifier);
  };

  const handleExport = () => {
    const headers = ["Name", "SKU", "Category", "Price", "Stock", "Status"];
    const rows = products.map((p) => [
      p?.name || "",
      p?.sku || "",
      p?.category || "",
      p?.price || 0,
      p?.stock || 0,
      p?.status || "draft",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `products-export-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // --------- FORM MODE ---------
  if (mode === "form") {
    return (
      <ProductForm
        initialData={editingProduct || emptyProduct}
        onCancel={handleCancelForm}
        onSave={handleSaveProduct}
        loading={saving}
        error={error}
        onClearError={() => setError(null)}
      />
    );
  }

  // --------- TABLE MODE ---------
  return (
    <div className="products-page">
      {error && (
        <div className="error-toast">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="products-header">
        <div className="products-header-top">
          <div className="products-header-left">
            <h1 className="products-title">Products</h1>
            <div className="products-breadcrumbs">
              <span className="breadcrumb-link">Dashboard</span>
              <FiChevronRight className="breadcrumb-separator-icon" />
              <span className="breadcrumb-current">Product List</span>
            </div>
          </div>

          <div className="products-header-right">
            <button
              className="btn btn-icon-only"
              type="button"
              onClick={fetchProducts}
              disabled={loading}
              title="Refresh"
            >
              <FiRefreshCw className={`btn-icon ${loading ? "spinning" : ""}`} />
            </button>
            <button
              className="btn btn-outline"
              type="button"
              onClick={handleExport}
              disabled={loading || products.length === 0}
            >
              <BiExport className="btn-icon" />
              <span>Export</span>
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={handleAddProductClick}
              disabled={loading}
            >
              <FiPlus className="btn-icon" />
              <span>Add Product</span>
            </button>
          </div>
        </div>

        <div className="products-header-bottom">
          <div className="products-search-wrapper">
            <FiSearch className="products-search-icon" />
            <input
              type="text"
              className="products-search-input"
              placeholder="Search product..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="products-filters-right">
            <button
              type="button"
              className="filters-btn"
              onClick={() => console.log("Filters clicked")}
            >
              <FiFilter className="filters-icon" />
              <span>Filters</span>
            </button>
          </div>
        </div>
      </div>

      <div className="products-card">
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <span>Loading products...</span>
          </div>
        )}

        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th className="col-product">
                  <div className="product-header-cell">
                    <input type="checkbox" />
                    <span>Product</span>
                  </div>
                </th>
                <th className="col-text"><span>Category</span></th>
                {/* <th className="col-number"><span>Stock</span></th> */}
                <th className="col-number"><span>Price</span></th>
                <th className="col-status"><span>Status</span></th>
                <th className="col-action"><span>Action</span></th>
              </tr>
            </thead>
            <tbody>
              {!loading && totalItems === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-table-cell">
                    No products yet. Click <strong>Add Product</strong> to create one.
                  </td>
                </tr>
              ) : (
                currentItems.map((product) => (
                  <tr key={product.id || product.sku}>
                    <td className="col-product">
                      <div className="product-cell">
                        <input type="checkbox" className="row-checkbox" />
                        <div
                          className={`product-thumbnail ${product.imageUrl ? "clickable" : ""}`}
                          onClick={() =>
                            product.imageUrl && openLightbox(product.imageUrl, product.name)
                          }
                        >
                          {product.imageUrl ? (
                            <img
                              src={getImageUrl(product.imageUrl)}
                              alt={product.name || "Product"}
                            />
                          ) : (
                            <div className="thumbnail-placeholder" />
                          )}
                        </div>
                        <div className="product-info">
                          <div className="product-name">{product.name || "-"}</div>
                          <div className="product-sub">{product.sku || "-"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="col-text">{product.category || "-"}</td>
                    {/* <td className="col-number">{(product.stock ?? 0).toLocaleString()}</td> */}
                    <td className="col-number">
                      {typeof product.price === "number" ? `₹${product.price.toFixed(2)}` : "-"}
                    </td>
                    <td className="col-status">
                      <span className={`status-pill ${(product.status || "draft").replace(/_/g, "-")}`}>
                        {(product.status || "draft").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="col-action">
                      <div className="action-buttons-product">

                        <button
                          type="button"
                          className="icon-button small"
                          title="Edit"
                          onClick={() => handleEditProduct(product)}
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          className="icon-button small danger"
                          title="Delete"
                          onClick={() => handleDeleteProduct(product)}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && (
          <div className="products-pagination">
            <div className="pagination-info">
              {`Showing ${startIndex + 1}-${Math.min(startIndex + PAGE_SIZE, totalItems)} of ${totalItems}`}
            </div>
            <div className="pagination-controls">
              <button
                type="button"
                className="page-button"
                onClick={() => changePage(safePage - 1)}
                disabled={safePage === 1}
              >
                ‹
              </button>

              {paginationRange.map((item, index) => {
                if (item === DOTS) {
                  return (
                    <button
                      key={`dots-${index}`}
                      type="button"
                      className="page-button page-dots"
                      disabled
                    >
                      …
                    </button>
                  );
                }

                const page = item;
                return (
                  <button
                    key={page}
                    type="button"
                    className={`page-button ${page === safePage ? "active" : ""}`}
                    onClick={() => changePage(page)}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                type="button"
                className="page-button"
                onClick={() => changePage(safePage + 1)}
                disabled={safePage === totalPages}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>
      {lightboxImage && (
  <div className="image-lightbox-overlay" onClick={closeLightbox}>
    <div
      className="image-lightbox-content"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="image-lightbox-close"
        onClick={closeLightbox}
      >
        ×
      </button>
      <img src={lightboxImage.src} alt={lightboxImage.alt} />
    </div>
  </div>
)}
    </div>
  );
};

export default Products;